import React, { useState, useRef, useEffect } from 'react';
import { ToastContainer, toast } from "react-toastify";
import { CareersForm, saveCareersFormDB } from '../../api/slices/careersSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../api/store';

export interface JobPosition {
    id: number;
    title: string;
    department: string;
    type: string;
    location: string;
    description: string;
    requirements: string[];
}

interface JobModalProps {
    selectedJob: JobPosition | null;
    onClose: () => void;
    onApply: (job: JobPosition) => void;
}

const JobModal: React.FC<JobModalProps> = ({ selectedJob, onClose, onApply }) => {
    const [formData, setFormData] = useState({
        doctorName: '',
        age: 0,
        workExperience: '',
        speciality: '',
        whatsappNumber: '',
        email: '',
        degree: ''
    });

    const modalRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch<AppDispatch>()
    const [isLoading, setIsLoading] = useState(false);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (selectedJob) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [selectedJob]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setIsLoading(true);

            // Validate required fields
            const requiredFields = {
                name: formData.doctorName.trim(),
                age: formData.age,
                speciality: formData.speciality.trim(),
                degree: formData.degree.trim(),
                email: formData.email.trim(),
                whatsapp: formData.whatsappNumber.trim(),
                experience: formData.workExperience.trim(),
            };

            const missingFields = Object.entries(requiredFields)
                .filter(([_, value]) => !value)
                .map(([key]) => key);

            if (missingFields.length > 0) {
                toast.error(`Please fill in all required fields: ${missingFields.join(", ")}`);
                return;
            }

            // Validate email format
            const emailRegex = /\S+@\S+\.\S+/;
            if (!emailRegex.test(formData.email)) {
                toast.error("Please enter a valid email address");
                return;
            }

            // Validate phone number
            const phoneRegex = /^[0-9]{10,15}$/;
            if (!phoneRegex.test(formData.whatsappNumber)) {
                toast.error("Please enter a valid WhatsApp number (10-15 digits)");
                return;
            }

            // Validate age
            const age = parseInt(formData.age.toString());
            if (isNaN(age) || age < 22 || age > 70) {
                toast.error("Please enter a valid age (22-70)");
                return;
            }

            // Validate experience
            const experience = parseInt(formData.workExperience);
            if (isNaN(experience) || experience < 0 || experience > 50) {
                toast.error("Please enter valid work experience (0-50 years)");
                return;
            }

            const careersFormData: CareersForm = {
                name: formData.doctorName.trim(),
                age: age,
                specialty: formData.speciality.trim(),
                degree: formData.degree.trim(),
                email: formData.email.trim(),
                contact: formData.whatsappNumber.trim(),
                experience: formData.workExperience.trim(),
                status: 1,
            };
            const resultAction = await dispatch(saveCareersFormDB(careersFormData));

            if (saveCareersFormDB.fulfilled.match(resultAction)) {
                toast.success("Application submitted successfully!");

                // Reset form
                setFormData({
                    doctorName: '',
                    age: 0,
                    workExperience: '',
                    speciality: '',
                    whatsappNumber: '',
                    email: '',
                    degree: ''
                });

                setTimeout(() => {
                    if (selectedJob) {
                        onApply(selectedJob);
                    }
                }, 5000);
            }
            else {
                const errorMessage = (resultAction.payload as string) || resultAction.error?.message || "Failed to save application";
                toast.error(errorMessage);
                setTimeout(() => {
                    onClose();
                }, 5000);
            }
        } catch (error: any) {
            console.error('Application submission error:', error);
            toast.error(
                error.message || "There was an error processing your application. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    if (!selectedJob) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div
                ref={modalRef}
                className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                        <h3 className="text-2xl font-bold text-gray-800">Apply for {selectedJob.title}</h3>
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="text-gray-500 hover:text-gray-700 text-2xl transition-colors disabled:opacity-50"
                            aria-label="Close modal"
                        >
                            &times;
                        </button>
                    </div>

                    <div className='overflow-y-auto max-h-[70vh] py-2 pr-2'>
                        <div className="flex flex-wrap gap-2 mb-6">
                            <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded">
                                {selectedJob.type}
                            </span>
                            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded">
                                {selectedJob.location}
                            </span>
                        </div>

                        <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">Job Description</h4>
                            <p className="text-gray-600">{selectedJob.description}</p>
                        </div>

                        <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">Requirements</h4>
                            <ul className="list-disc pl-5 text-gray-600">
                                {selectedJob.requirements.map((req, index) => (
                                    <li key={index}>{req}</li>
                                ))}
                            </ul>
                        </div>

                        {/* Application Form */}
                        <div className="mt-8 pt-6 border-t border-gray-200 mx-1">
                            <h4 className="text-xl font-semibold text-gray-800 mb-6">Application Form</h4>

                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                    <label htmlFor="doctorName" className="block text-sm font-medium text-gray-700 mb-1">
                                        Doctor Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="doctorName"
                                        name="doctorName"
                                        value={formData.doctorName}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                                        Age *
                                    </label>
                                    <input
                                        type="number"
                                        id="age"
                                        name="age"
                                        value={formData.age || ''}
                                        onChange={handleInputChange}
                                        required
                                        min="22"
                                        max="70"
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="Enter your age"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="workExperience" className="block text-sm font-medium text-gray-700 mb-1">
                                        Work Experience (years) *
                                    </label>
                                    <input
                                        type="number"
                                        id="workExperience"
                                        name="workExperience"
                                        value={formData.workExperience}
                                        onChange={handleInputChange}
                                        required
                                        min="0"
                                        max="50"
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="Years of experience"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="speciality" className="block text-sm font-medium text-gray-700 mb-1">
                                        Speciality *
                                    </label>
                                    <input
                                        type="text"
                                        id="speciality"
                                        name="speciality"
                                        value={formData.speciality}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="Your speciality"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                        WhatsApp Number *
                                    </label>
                                    <input
                                        type="tel"
                                        id="whatsappNumber"
                                        name="whatsappNumber"
                                        value={formData.whatsappNumber}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="1234567890"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="your.email@example.com"
                                    />
                                </div>

                                <div className="mb-4 md:col-span-2">
                                    <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-1">
                                        Degree/Qualifications *
                                    </label>
                                    <input
                                        type="text"
                                        id="degree"
                                        name="degree"
                                        value={formData.degree}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isLoading}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors disabled:opacity-50"
                                        placeholder="e.g., MBBS, MD, MS, etc."
                                    />
                                </div>

                                <div className="md:col-span-2 flex justify-end gap-4 mt-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isLoading}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Submitting...' : 'Submit Application'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={5000} />
        </div>
    );
};

export default JobModal;